import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'

import { api, ApiError } from '@/lib/api'
import { setMe, setTimeZone as setRuleTimeZone } from '@/lib/logic'
import { setTimeZone } from '@/lib/utils'
import type { Me, RecordScope } from '@/lib/types'

interface AuthValue {
  me: Me | null
  loading: boolean
  /** Mirrors the server's Principal.has (deps.py) — superuser bypasses. */
  can: (code: string) => boolean
  /** Mirrors Principal.scope_for. */
  scopeFor: (code: string) => RecordScope
  /** True if the caller holds any of the codes. Used to hide whole nav sections. */
  canAny: (...codes: string[]) => boolean
  logout: () => Promise<void>
  refresh: () => Promise<unknown>
}

const AuthContext = React.createContext<AuthValue>({
  me: null,
  loading: true,
  can: () => false,
  scopeFor: () => 'none',
  canAny: () => false,
  logout: async () => {},
  refresh: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<Me>('/api/auth/me')
      } catch (error) {
        // Not signed in is a normal state, not a failure to retry.
        if (error instanceof ApiError && error.isUnauthenticated) return null
        throw error
      }
    },
    retry: false,
    staleTime: 60_000,
  })

  const logoutMutation = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    // Nothing on screen is read again: it is all cleared (§P24).
    meta: { reads: true },
    onSettled: () => {
      queryClient.setQueryData(['me'], null)
      queryClient.clear()
    },
  })

  const me = query.data ?? null
  // Set before anything below renders a time: every time is the organisation's.
  setTimeZone(me?.timezone)
  setRuleTimeZone(me?.timezone)
  // Rules may say {"me": "email"}: the person's own record is the one with their email.
  React.useEffect(() => {
    setMe(me ? { id: me.user.id, email: me.user.email, name: me.user.name ?? '' } : null)
  }, [me])

  const value = React.useMemo<AuthValue>(() => {
    const can = (code: string) => {
      if (!me) return false
      return me.is_superuser || code in me.permissions
    }
    return {
      me,
      loading: query.isLoading,
      can,
      scopeFor: (code: string) => {
        if (!me) return 'none'
        if (me.is_superuser) return 'all'
        return me.permissions[code] ?? 'none'
      },
      canAny: (...codes: string[]) => codes.some(can),
      logout: async () => {
        await logoutMutation.mutateAsync()
      },
      refresh: () => query.refetch(),
    }
  }, [me, query, logoutMutation])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return React.useContext(AuthContext)
}
